import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { env } from '@/lib/env';
import { mockCaptures, mockDashboard } from '@/lib/mockData';

type Tab = 'prompts' | 'captures' | 'system';

const DEFAULT_PROMPTS = {
  'capture-parser': 'See /prompts/capture-parser.md (loaded from bundled Lambda source in production).',
  editor: 'See /prompts/editor.md (loaded from bundled Lambda source in production).',
  'back-page': 'See /prompts/back-page.md (loaded from bundled Lambda source in production).',
};

export function DeskRoute() {
  const [tab, setTab] = useState<Tab>('prompts');

  return (
    <div className="min-h-screen px-8 py-10 max-w-5xl mx-auto">
      <Link
        to="/wall"
        className="inline-flex items-center gap-1.5 text-body-sm text-inkFaded mb-6 hover:text-tomato"
      >
        <ArrowLeft size={14} /> back to the wall
      </Link>
      <header className="mb-8">
        <span className="text-eyebrow uppercase tracking-[0.18em] text-tomato">
          The Daily · Desk
        </span>
        <h1 className="font-serif text-display-md mt-2">Admin</h1>
        <p className="font-serif italic text-body-lg text-inkSoft mt-1">
          Edit the editor's voice. Audit what Claude got wrong yesterday. Force a regenerate.
        </p>
      </header>

      <nav className="flex gap-1 mb-6 border-b border-black/5">
        {(['prompts', 'captures', 'system'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx(
              'px-4 py-2 text-body-sm capitalize border-b-2 -mb-px transition-colors',
              tab === t
                ? 'border-tomato text-ink'
                : 'border-transparent text-inkFaded hover:text-ink',
            )}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === 'prompts' && <PromptsPanel />}
      {tab === 'captures' && <CapturesPanel />}
      {tab === 'system' && <SystemPanel />}
    </div>
  );
}

function PromptsPanel() {
  const [values, setValues] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_PROMPTS;
    return {
      'capture-parser': localStorage.getItem('daily:prompt:capture-parser') ?? DEFAULT_PROMPTS['capture-parser'],
      editor: localStorage.getItem('daily:prompt:editor') ?? DEFAULT_PROMPTS.editor,
      'back-page': localStorage.getItem('daily:prompt:back-page') ?? DEFAULT_PROMPTS['back-page'],
    };
  });
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const onSave = (key: keyof typeof DEFAULT_PROMPTS) => {
    localStorage.setItem(`daily:prompt:${key}`, values[key]);
    setSavedAt(`${key} · ${format(new Date(), 'HH:mm:ss')}`);
  };

  return (
    <div className="flex flex-col gap-8">
      <p className="text-body-sm text-inkFaded">
        In mock mode, edits save to localStorage. In production this panel will POST to{' '}
        <code className="font-mono text-[12px] bg-cardWarm px-1.5 py-0.5 rounded">/api/admin/prompts</code>{' '}
        and trigger a Lambda redeploy of the prompt asset.
      </p>
      {(['capture-parser', 'editor', 'back-page'] as const).map((key) => (
        <section key={key} className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-display-sm">{key}</h2>
            <button
              type="button"
              onClick={() => onSave(key)}
              className="text-body-sm text-tomato hover:underline"
            >
              save
            </button>
          </div>
          <textarea
            value={values[key]}
            onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
            rows={key === 'capture-parser' ? 14 : 8}
            className="w-full font-mono text-[13px] leading-relaxed p-4 rounded-md border border-black/10 bg-card resize-y"
          />
        </section>
      ))}
      {savedAt && (
        <p className="text-body-sm text-sage">
          Saved · <span className="font-mono">{savedAt}</span>
        </p>
      )}
    </div>
  );
}

function CapturesPanel() {
  const { data } = useQuery({
    queryKey: ['desk-captures'],
    queryFn: () => (env.mock ? Promise.resolve(mockCaptures()) : api.listCaptures()),
  });

  const rows = data ?? [];
  return (
    <table className="w-full text-body-sm border-collapse">
      <thead>
        <tr className="text-left text-eyebrow uppercase tracking-[0.18em] text-inkFaded border-b border-black/5">
          <th className="py-3 pr-3">Type</th>
          <th className="py-3 pr-3">Caption</th>
          <th className="py-3 pr-3">Author</th>
          <th className="py-3 pr-3">When</th>
          <th className="py-3 pr-3">Confidence</th>
          <th className="py-3">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.id} className="border-b border-black/5">
            <td className="py-3 pr-3 font-mono text-[11px] text-inkFaded">{c.captureType}</td>
            <td className="py-3 pr-3 font-serif italic">{c.caption ?? '—'}</td>
            <td className="py-3 pr-3 text-inkSoft">{c.author ?? '—'}</td>
            <td className="py-3 pr-3 text-inkFaded font-mono text-[11px]">
              {format(new Date(c.uploadedAt), 'd MMM HH:mm')}
            </td>
            <td className="py-3 pr-3">{c.confidence?.toFixed(2) ?? '—'}</td>
            <td className="py-3">
              <span
                className={clsx(
                  'inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.06em]',
                  c.status === 'ready' && 'bg-sage/15 text-sageDeep',
                  c.status === 'processing' && 'bg-mustard/20 text-terracotta',
                  c.status === 'failed' && 'bg-tomato/15 text-tomatoDeep',
                )}
              >
                {c.status}
              </span>
            </td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={6} className="py-6 text-center font-serif italic text-inkFaded">
              No captures yet.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function SystemPanel() {
  const dashboard = useMemo(() => (env.mock ? mockDashboard() : null), []);
  const envRows = [
    ['Mode', env.mock ? 'mock (no AWS)' : 'live'],
    ['Domain', env.domain],
    ['API base', env.apiBase || '(unset)'],
    ['AppSync URL', env.appsyncUrl || '(unset)'],
    ['Cognito pool', env.cognitoUserPoolId || '(unset)'],
    ['Household', env.householdId],
  ];

  return (
    <div className="flex flex-col gap-10">
      <section>
        <h2 className="text-eyebrow uppercase tracking-[0.18em] text-inkFaded mb-3">
          Environment
        </h2>
        <table className="text-body-sm">
          <tbody>
            {envRows.map(([k, v]) => (
              <tr key={k}>
                <td className="py-1 pr-6 text-inkFaded">{k}</td>
                <td className="py-1 font-mono text-[12px]">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-eyebrow uppercase tracking-[0.18em] text-inkFaded">Manual</h2>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={env.mock}
            onClick={() => fetch(`${env.apiBase}/admin/headline/regenerate`, { method: 'POST' })}
            className="px-4 py-2 rounded-md bg-tomato text-white text-body-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Regenerate headline
          </button>
          <button
            type="button"
            disabled={env.mock}
            onClick={() => fetch(`${env.apiBase}/admin/memory/regenerate`, { method: 'POST' })}
            className="px-4 py-2 rounded-md bg-card border border-black/10 text-body-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Regenerate memory
          </button>
        </div>
        {env.mock && (
          <p className="text-body-sm text-inkFaded">
            Disabled in mock mode — set <code className="font-mono text-[12px]">VITE_API_BASE</code> to enable.
          </p>
        )}
      </section>

      {dashboard && (
        <section>
          <h2 className="text-eyebrow uppercase tracking-[0.18em] text-inkFaded mb-3">
            Current snapshot
          </h2>
          <pre className="bg-card rounded-md p-5 shadow-sm text-[11px] font-mono overflow-x-auto leading-relaxed">
            {JSON.stringify(dashboard, null, 2)}
          </pre>
        </section>
      )}
    </div>
  );
}
